import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, Filter, Calendar, ArrowRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SessionCard } from '../components/SessionCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const countryFlags = {
  DE: '🇩🇪', NL: '🇳🇱', IT: '🇮🇹', FR: '🇫🇷', PL: '🇵🇱',
  ES: '🇪🇸', PT: '🇵🇹', BE: '🇧🇪', SE: '🇸🇪', FI: '🇫🇮'
};

const countryNames = {
  DE: 'Germany', NL: 'Netherlands', IT: 'Italy', FR: 'France', PL: 'Poland',
  ES: 'Spain', PT: 'Portugal', BE: 'Belgium', SE: 'Sweden', FI: 'Finland'
};

export const SessionsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  const typeFilter = searchParams.get('type') || 'all';
  const countryFilter = searchParams.get('country') || 'all';

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        let url = `${API}/sessions`;
        const params = [];
        if (typeFilter !== 'all') params.push(`session_type=${typeFilter}`);
        if (countryFilter !== 'all') params.push(`country_code=${countryFilter}`);
        if (params.length > 0) url += `?${params.join('&')}`;
        
        const response = await axios.get(url);
        setSessions(response.data);
      } catch (error) {
        console.error('Error fetching sessions:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSessions();
  }, [typeFilter, countryFilter]);

  const handleTypeChange = (value) => {
    if (value === 'all') {
      searchParams.delete('type');
    } else {
      searchParams.set('type', value);
    }
    setSearchParams(searchParams);
  };

  const handleCountryChange = (value) => {
    if (value === 'all') {
      searchParams.delete('country');
    } else {
      searchParams.set('country', value);
    }
    setSearchParams(searchParams);
  };

  const filteredSessions = sessions.filter(session => 
    session.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    session.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const freeSessions = filteredSessions.filter(s => s.session_type === 'free_session');
  const paidSessions = filteredSessions.filter(s => s.session_type === 'paid_masterclass');

  return (
    <div data-testid="sessions-page" className="min-h-screen pt-20 pb-16 bg-[#FFFBF8]">
      {/* Header */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="max-w-3xl">
            <Badge className="bg-[#0AA06A] text-white mb-4 px-3 py-1">
              Live & Interactive
            </Badge>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#002A76] mb-4">
              Learn From Expats, Live
            </h1>
            <p className="text-lg text-gray-600">
              Join our free live sessions and premium masterclasses to learn about study, work, and settlement opportunities in Europe.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-16 md:top-20 z-40 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                data-testid="session-search"
                placeholder="Search sessions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 rounded-lg border-gray-200"
              />
            </div>

            {/* Type Filter */}
            <Select value={typeFilter} onValueChange={handleTypeChange}>
              <SelectTrigger data-testid="filter-type" className="w-full md:w-48 h-11 rounded-lg">
                <SelectValue placeholder="Session Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sessions</SelectItem>
                <SelectItem value="free_session">Free Sessions</SelectItem>
                <SelectItem value="paid_masterclass">Masterclasses</SelectItem>
              </SelectContent>
            </Select>

            {/* Country Filter */}
            <Select value={countryFilter} onValueChange={handleCountryChange}>
              <SelectTrigger data-testid="filter-country" className="w-full md:w-48 h-11 rounded-lg">
                <SelectValue placeholder="Select Country" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {Object.entries(countryNames).map(([code, name]) => (
                  <SelectItem key={code} value={code}>
                    {countryFlags[code]} {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Sessions List */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                  <div className="aspect-video bg-gray-200" />
                  <div className="p-5">
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-3" />
                    <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl">
              <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold text-[#002A76] mb-2">No sessions found</h3>
              <p className="text-gray-600 mb-6">Try adjusting your filters or check back later</p>
              <Button 
                onClick={() => {
                  setSearchTerm('');
                  setSearchParams({});
                }}
                className="bg-[#002A76] hover:bg-[#001F5C] text-white rounded-full"
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="space-y-12">
              {/* Free Sessions */}
              {(typeFilter === 'all' || typeFilter === 'free_session') && freeSessions.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-[#0AA06A] flex items-center justify-center">
                      <span className="text-white font-bold">{freeSessions.length}</span>
                    </div>
                    <h2 className="text-2xl font-bold text-[#002A76]">Free Live Sessions</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {freeSessions.map((session) => (
                      <SessionCard key={session.id} session={session} />
                    ))}
                  </div>
                </div>
              )}

              {/* Paid Masterclasses */}
              {(typeFilter === 'all' || typeFilter === 'paid_masterclass') && paidSessions.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-[#F59E0B] flex items-center justify-center">
                      <span className="text-white font-bold">{paidSessions.length}</span>
                    </div>
                    <h2 className="text-2xl font-bold text-[#002A76]">Premium Masterclasses</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {paidSessions.map((session) => (
                      <SessionCard key={session.id} session={session} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#002A76] rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Can't find what you're looking for?
            </h2>
            <p className="text-gray-300 mb-6">
              Book a personalized 1:1 call with our expat advisors
            </p>
            <Link to="/book-call">
              <Button className="bg-[#0AA06A] hover:bg-[#088A5B] text-white rounded-full px-8 py-3 font-semibold">
                Book 1:1 Call
                <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SessionsPage;
